import {useEffect, useRef, useState} from "react";
import api from "./api";
import {createSecureUuid} from "./secureUuid";
import {getBrowserLocation} from "./attendanceLocation";

export default function useAttendanceAttempt(kiosk = false) {
  const current = useRef(null), pending = useRef(null), retries = useRef(0);
  const [locating, setLocating] = useState(false);
  useEffect(() => {
    const cancel=()=>current.current?.controller.abort();
    window.addEventListener("pagehide",cancel);
    return () => {cancel();current.current=null;window.removeEventListener("pagehide",cancel);};
  }, []);
  const report = (attempt, code, diagnostics = {}) => {
    if (!attempt?.reference || attempt.reported) return;
    attempt.reported = true;
    // One summary per attempt; never send coordinates, credentials, PINs or raw UA.
    Promise.resolve(api.post(`/staff-attendance/${kiosk ? "kiosk/" : ""}diagnostics`, {
      support_reference: attempt.reference, action: attempt.action.split(":")[0], error_code: code,
      retry_count: retries.current, frontend_version: process.env.REACT_APP_ATTENDANCE_VERSION || "unversioned",
      browser_family: /OPR\//.test(navigator.userAgent) ? "Opera" : /Chrome\//.test(navigator.userAgent) ? "Chromium" : "Other",
      platform: /Android/.test(navigator.userAgent) ? "Android" : "Other", ...diagnostics,
    })).catch(() => {});
  };
  return {
    locating,
    begin(action) {
      if (current.current) return false;
      current.current = {action, controller: new AbortController()};
      try {
        current.current.reference = `ATT-${createSecureUuid()}`;
        if (!pending.current || pending.current.action !== action) pending.current = {action, key: createSecureUuid()};
      } catch (error) {current.current.error = error;}
      retries.current++;
      return true;
    },
    key() {return pending.current.key;},
    reference() {return current.current?.reference;},
    ensureActive() {if(!current.current || current.current.controller.signal.aborted) throw Object.assign(new Error("Location check cancelled."),{attendanceCode:"LOCATION_CANCELLED"});},
    async location() {
      const attempt = current.current;
      setLocating(true);
      try {
        if (attempt?.error) throw attempt.error;
        const result = await getBrowserLocation({signal: attempt?.controller.signal});
        if(attempt?.controller.signal.aborted) throw Object.assign(new Error("Location check cancelled."),{attendanceCode:"LOCATION_CANCELLED"});
        if (attempt) attempt.diagnostics = result.diagnostics;
        return {...result, support_reference: attempt?.reference};
      } catch (error) {
        error.support_reference = attempt?.reference;
        if (attempt) attempt.diagnostics = error.diagnostics;
        throw error;
      } finally {setLocating(false);}
    },
    fail(error) {
      const attempt = current.current;
      error.support_reference = error.response?.data?.detail?.support_reference || attempt?.reference;
      report(attempt, error.attendanceCode || error.response?.data?.detail?.code || (error.response ? "ATTENDANCE_API_ERROR" : "ATTENDANCE_NETWORK_ERROR"), attempt?.diagnostics);
      // Preserve the operation key only for ambiguous transport/server failures.
      if (error.attendanceCode || (error.response && error.response.status < 500)) pending.current = null;
      if (!error.attendanceCode && !error.response?.data?.detail?.code) error.attendanceCode=error.response ? "ATTENDANCE_API_ERROR" : "ATTENDANCE_NETWORK_ERROR";
    },
    success() {report(current.current, "SUCCESS", current.current?.diagnostics); pending.current = null; retries.current = 0;},
    end() {current.current?.controller.abort(); current.current = null;},
    cancel() {current.current?.controller.abort();},
  };
}
